// 游戏状态管理
import { SAVE_KEY, VERSION } from './config.js';
import { CLASSES } from './data/classes.js';
import { Character, upgradeToCharacter } from './data/character.js';

/** 全局游戏状态单例 — Character 实例，向后兼容所有 S.xxx 属性访问 */
export let S = new Character({
  scene:'lobby',
  name:'', cls:null,
  hp:100, maxHp:100, atk:10, def:5, spd:5,
  mp:50, maxMp:50,
  lv:1, exp:0, expNeed:50,
  gold:0, fatigue:156, maxFatigue:156,
  inventory:[], equipment:{}, flags:{}, regionProgress:{},
  statusEffects:{}, ownedEquipIds:[], npcBoosts:{}, enhanceLevels:{}, weaponPenalties:{}
});
// 附加非战斗字段（Character 未覆盖的游戏状态属性）
S.scene = 'lobby';
S.flags = {};
S.regionProgress = {};
S.combat = null;
S._lastDay = '';
S.sceneHistory = [];
S.currentDungeon = null;
S.currentDungeonDiff = null;
S._merchantItems = {};
S._dungeonRooms = null;
S._dungeonRoomIdx = 0;
S._dungeonModifier = null;
S._hellMode = false;
S.reputation = { hero: 0, shadow: 0, flags: {} };
S.activeQuests = [];
S.questProgress = {};

export function defaultState() {
  return {
    scene:'lobby',
    name:'', cls:null,
    hp:100, maxHp:100, atk:10, def:5, spd:5,
    mp:50, maxMp:50,
    lv:1, exp:0, expNeed:50,
    gold:0, fatigue:156, maxFatigue:156,
    inventory:[], equipment:{},
    combat:null,
    flags:{},
    regionProgress:{},
    ownedEquipIds:[],
    altarBuff:false,
    passiveProc:false,
    statusEffects:{},
    npcBoosts:{ atk:0, def:0, spd:0 },
    weaponPenalties:{},
    enhanceLevels:{},
    _lastDay:'',
    _skillCooldowns:{},
    _skillUpgrades:{},
    _lastSkillId:null,
    _activeBuffs:[],
    _activeDebuffs:{},
    _summonData:null,
    _burnTurns:0,
    _burnDmg:0,
  };
}

export function save() {
  try {
    const data = S.toJSON ? S.toJSON() : { ...S };
    // 附加非 Character 字段
    data.scene = S.scene;
    data.flags = S.flags;
    data.regionProgress = S.regionProgress;
    data.combat = S.combat;
    data._lastDay = S._lastDay;
    data.reputation = S.reputation;
    data.activeQuests = S.activeQuests;
    data.questProgress = S.questProgress;
    data.currentDungeon = S.currentDungeon;
    data.currentDungeonDiff = S.currentDungeonDiff;
    data._merchantItems = S._merchantItems;
    data._dungeonRooms = S._dungeonRooms;
    data._dungeonRoomIdx = S._dungeonRoomIdx;
    data._dungeonModifier = S._dungeonModifier;
    data._hellMode = S._hellMode;
    data.sceneHistory = S.sceneHistory;
    localStorage.setItem(SAVE_KEY, JSON.stringify(data));
    const el = document.getElementById('saveIndicator');
    if(el) { el.textContent = '💾 已保存'; el.classList.add('show'); setTimeout(()=>el.classList.remove('show'), 800); }
    cloudSave();
  } catch(e){}
}

export function load() {
  const raw = localStorage.getItem(SAVE_KEY);
  if(raw) {
    try {
      const parsed = JSON.parse(raw);
      // 清除 S 上的所有自有属性
      Object.keys(S).forEach(k => delete S[k]);
      // 恢复默认值 + 覆盖存档数据
      Object.assign(S, defaultState(), parsed);
      // 确保非 Character 字段也被恢复（不会被 defaultState 覆盖）
      if (parsed.scene) S.scene = parsed.scene;
      if (parsed.combat) S.combat = parsed.combat;
      if (parsed._lastDay) S._lastDay = parsed._lastDay;
      if (parsed.reputation) S.reputation = parsed.reputation;
      if (parsed.activeQuests) S.activeQuests = parsed.activeQuests;
      if (parsed.questProgress) S.questProgress = parsed.questProgress;
      if (parsed.currentDungeon) S.currentDungeon = parsed.currentDungeon;
      if (parsed.currentDungeonDiff) S.currentDungeonDiff = parsed.currentDungeonDiff;
      if (parsed._merchantItems) S._merchantItems = parsed._merchantItems;
      if (parsed._dungeonRooms) S._dungeonRooms = parsed._dungeonRooms;
      if (parsed._dungeonRoomIdx !== undefined) S._dungeonRoomIdx = parsed._dungeonRoomIdx;
      if (parsed._dungeonModifier) S._dungeonModifier = parsed._dungeonModifier;
      if (parsed._hellMode !== undefined) S._hellMode = parsed._hellMode;
      if (parsed.sceneHistory) S.sceneHistory = parsed.sceneHistory;
      // 确保 window.S 指向同一对象
      if(window.S !== S) window.S = S;
      return true;
    } catch(e) { console.warn('Load failed:', e); }
  }
  return false;
}

export function renderTopbar() {
  const el = document.getElementById('topbar');
  const bgmIcon = window.bgmOn ? '🔊' : '🔇';
  if(!S.name) {
    el.innerHTML = `<span style="color:var(--gold)">阿拉德战记</span><span>${VERSION}</span><button id="bgmBtn" onclick="toggleBGM()" style="background:none;border:none;cursor:pointer;font-size:14px;padding:0;">${bgmIcon}</button>`;
    return;
  }
  const clsName = S.cls ? CLASSES[S.cls].name : '';
  const faction = S.cls ? CLASSES[S.cls].faction : '';
  const factionColor = {
    '鬼剑士':'var(--purple)', '神枪手':'var(--orange)', '圣职者':'var(--gold)'
  }[faction] || 'var(--dim)';
  el.innerHTML = `<span style="color:${factionColor}">${S.name}</span>
    <span style="font-size:10px;">Lv.${S.lv}</span>
    <span>⚡${S.fatigue}/${S.maxFatigue}</span>
    <button id="bgmBtn" onclick="toggleBGM()" style="background:none;border:none;cursor:pointer;font-size:14px;padding:0;">${bgmIcon}</button>`;
}

export function renderStatusbar() {
  const el = document.getElementById('statusbar');
  if(!S.name) { el.innerHTML = ''; return; }
  if(!S.mp) { S.mp = 50; S.maxMp = 50; }
  const stats = window.getEquipStats ? window.getEquipStats() : { bonusAtk:0, bonusDef:0, bonusStr:0, bonusInt:0, bonusSpd:0 };
  const effAtk = S.atk + stats.bonusAtk + stats.bonusStr;
  const effDef = S.def + stats.bonusDef;
  const effSpd = S.spd + stats.bonusSpd;
  const statusIcons = getStatusIcons();
  const ccDisplay = (S.chaosCrystals > 0) ? `<span>💎 ${S.chaosCrystals}</span>` : '';
  el.innerHTML = `<span>❤️ ${S.hp}/${S.maxHp}</span>
    <span>💠 ${S.mp}/${S.maxMp}</span>
    <span>⚔️ ${effAtk}</span>
    <span>🛡️ ${effDef}</span>
    <span>💨 ${effSpd}</span>
    <span>💰 ${S.gold}</span>
    ${ccDisplay}
    ${statusIcons}
    <button onclick="showInventory()" style="font-size:10px;background:#1a1a24;color:#aaa;border:1px solid #333;padding:2px 8px;border-radius:4px;cursor:pointer;">🎒</button>`;
}

export function getStatusIcons() {
  if(!S.statusEffects) return '';
  let out = '';
  if(S.statusEffects.poison) out += '<span title="中毒" style="color:#8a2">☠</span>';
  if(S.statusEffects.freeze) out += '<span title="冰冻" style="color:#5af">❄</span>';
  if(S.statusEffects.curse) out += '<span title="诅咒" style="color:#b7f">◈</span>';
  return out;
}

